import React from "react";

export function TextField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-secondary-dark mb-1.5">{props.label}</span>
      <input
        type="text"
        value={props.value}
        required={props.required}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full border border-fourth-light rounded-lg px-3 py-2 text-sm outline-none focus:border-gold transition-colors bg-white"
      />
    </label>
  );
}

export function TextAreaField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-secondary-dark mb-1.5">{props.label}</span>
      <textarea
        value={props.value}
        rows={props.rows ?? 3}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full border border-fourth-light rounded-lg px-3 py-2 text-sm outline-none focus:border-gold transition-colors resize-y bg-white"
      />
    </label>
  );
}

export function SelectField<T extends string>(props: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-secondary-dark mb-1.5">{props.label}</span>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value as T)}
        className="w-full border border-fourth-light rounded-lg px-3 py-2 text-sm outline-none focus:border-gold transition-colors bg-white"
      >
        {props.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SegmentedField<T extends string>(props: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="block mb-4">
      <span className="block text-sm text-secondary-dark mb-1.5">{props.label}</span>
      <div className="inline-flex p-1 rounded-full bg-c-gray border border-fourth-light gap-1">
        {props.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => props.onChange(opt.value)}
            className={`px-4 py-1.5 rounded-full text-sm transition-all ${
              props.value === opt.value
                ? "bg-white text-secondary-dark shadow-sm font-medium"
                : "text-secondary-light hover:text-secondary-dark"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </label>
  );
}

export function FieldSection(props: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-fourth-light p-6 mb-6">
      <h2 className="text-sm font-semibold text-secondary-dark uppercase tracking-wide mb-4 pb-3 border-b border-fourth-light">
        {props.title}
      </h2>
      {props.children}
    </section>
  );
}
